'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { connectSocket, isSessionExpiredPayload } from '../lib/socket';
import { getLastUserId, setLastUserId, clearAllCache } from '@/lib/cache';
import { clearDrafts } from '@/lib/drafts/store';
import { lerAcesso } from '@/lib/auth/acesso';

type SocketContextType = {
  socket: Socket | null;
  socketReady: boolean;
};

const SocketContext = createContext<SocketContextType>({
  socket: null,
  socketReady: false,
});

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketReady, setSocketReady] = useState(false);

  useEffect(() => {
    const token = lerAcesso();
    const userId = localStorage.getItem('user_id');

    if (!token) {
      // O portão de sessão (`(private)/PortaoDeSessao`) só monta este provider depois da renovação inicial:
      // sem access aqui é bug de montagem, não sessão vencida.
      console.warn('SocketProvider montou sem access em memória; o socket não conecta.');
      return;
    }

    // Verificar cache e usuário em background
    (async () => {
      if (userId) {
        try {
          const lastUserId = await getLastUserId();

          if (lastUserId && lastUserId !== userId) {
            console.log('🔄 User changed, clearing cache...');
            await clearAllCache();
            // Mesmo gatilho, mesmo dono que sai: o rascunho do `lastUserId`
            // está em texto claro no localStorage e o cache que acabou de ir
            // embora era cifrado. Só a chave dele — a do usuário atual fica.
            clearDrafts(lastUserId);
          }

          if (lastUserId !== userId) {
            await setLastUserId(userId);
          }
        } catch (err) {
          console.error('Error in socket cache verification:', err);
        }
      }
    })();

    const socketInstance = connectSocket(token);
    setSocket(socketInstance);

    // Se o socket já estiver conectado (ex: veio do login), define como pronto imediatamente
    if (socketInstance.connected) {
      setSocketReady(true);
      console.log('✅ Socket já estava conectado:', socketInstance.id);
    }

    const onConnect = () => {
      setSocketReady(true);
      console.log('✅ Socket conectado:', socketInstance.id);
    };

    const onDisconnect = (reason: string) => {
      setSocketReady(false);
      console.warn('❌ Socket desconectado:', reason);
    };

    const onError = (error: unknown) => {
      // Sessão expirada já é reciclada em lib/socket — console.error aqui só
      // dispararia o overlay do Next em dev para um fluxo que se recupera sozinho.
      if (isSessionExpiredPayload(error)) return;
      console.error('🚨 Socket error:', error);
    };

    const onReconnect = (attemptNumber: number) => {
      console.log(`🔄 Socket reconectado após ${attemptNumber} tentativas`);
    };

    // warn, não error: dispara a cada tentativa falha durante uma queda — o
    // manager re-tenta pra sempre e error-level viraria spam de overlay em dev.
    const onReconnectError = (error: Error) => {
      console.warn('🔁 Erro ao reconectar socket:', error);
    };

    socketInstance.on('connect', onConnect);
    socketInstance.on('disconnect', onDisconnect);
    socketInstance.on('error', onError);
    // reconnect/reconnect_error são eventos do MANAGER (socket.io), não do socket.
    socketInstance.io.on('reconnect', onReconnect);
    socketInstance.io.on('reconnect_error', onReconnectError);

    return () => {
      socketInstance.off('connect', onConnect);
      socketInstance.off('disconnect', onDisconnect);
      socketInstance.off('error', onError);
      socketInstance.io.off('reconnect', onReconnect);
      socketInstance.io.off('reconnect_error', onReconnectError);
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, socketReady }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);